/**
 * MovementConfig — the single tunable table for the player controller.
 *
 * Everything the movement + camera code reads lives here. Units are metres,
 * seconds and radians. Numbers are tuned for Call of Duty style handling:
 * high ground acceleration, short crisp stops, limited-but-present air
 * control, and a camera that always settles.
 *
 * Owner: player team. Other teams: read it, never mutate it at runtime —
 * clone if you need a variant.
 */

const DEG = Math.PI / 180;

export const MovementConfig = {
  /* ---------------------------------------------------------------- body */
  radius: 0.32,
  standHeight: 1.80,
  crouchHeight: 1.15,
  eyeStand: 1.65,
  eyeCrouch: 0.95,
  eyeSlide: 0.78,
  /** How fast the eye chases its target height (critically damped, rad/s). */
  eyeSpring: 16.0,
  eyeSpringSlide: 26.0,

  /* ------------------------------------------------------------ movement */
  speedWalk: 4.4,          // W held, hip fire
  speedSprint: 6.4,        // shift
  speedTacSprint: 7.8,     // double-tap shift
  speedCrouch: 2.4,
  speedAds: 2.6,           // fully aimed down sights
  /** Directional scaling — you are slower going backwards / sideways. */
  scaleBack: 0.72,
  scaleStrafe: 0.92,

  accelGround: 60.0,       // m/s^2, snappy
  accelAir: 7.5,           // m/s^2, limited air steering
  airWishSpeedCap: 3.4,    // Quake-style projected wish speed cap in air
  frictionGround: 6.0,     // 1/s exponential-ish drag
  frictionStopSpeed: 1.6,  // below this, friction uses a constant floor (crisp stops)
  frictionCrouch: 7.5,
  frictionAir: 0.06,

  /* ---------------------------------------------------------------- jump */
  gravity: 9.81 * 2.2,     // 21.582 m/s^2 — CONTRACTS.md
  jumpApex: 0.55,          // metres; jump impulse is derived from this
  coyoteTime: 0.12,
  jumpBuffer: 0.15,
  jumpCooldown: 0.10,
  /** Extra horizontal boost when jumping while sprinting (very small). */
  jumpForwardBoost: 0.35,

  /* -------------------------------------------------------------- ground */
  stepHeight: 0.45,
  groundSnap: 0.38,        // pull back to ground when walking off a small lip
  maxGroundAngleCos: Math.cos(48 * DEG),
  groundProbe: 0.14,

  /* --------------------------------------------------------------- slide */
  slideMinSpeed: 5.2,      // must be at least this fast to start a slide
  slideBoost: 1.16,        // multiplier on entry speed
  slideBoostMin: 7.4,      // entry speed floor
  slideSpeedCap: 8.8,
  slideDuration: 0.90,
  slideFrictionStart: 0.55,
  slideFrictionEnd: 4.6,
  slideFrictionCurve: 2.0, // exponent on t/duration
  slideExitSpeed: 2.6,     // drop out of the slide below this
  slideCooldown: 0.75,
  slideSteer: 2.2,         // rad/s of heading steering allowed mid-slide
  slideRoll: 7.0 * DEG,
  slideJumpKeep: 0.92,     // fraction of horizontal speed kept on slide-cancel

  /* -------------------------------------------------------------- mantle */
  mantleReach: 0.62,       // forward probe beyond the capsule surface
  mantleProbeHeight: 1.02, // chest height above the feet for the forward ray
  vaultMaxHeight: 1.05,
  mantleMaxHeight: 2.10,
  mantleMinHeight: 0.34,
  vaultDuration: 0.42,
  mantleDuration: 0.76,
  mantleClearDepth: 0.55,  // how far past the lip we must land
  mantleMinSpeedAuto: 3.2, // auto-vault trigger when moving at least this fast
  mantleExitSpeed: 2.2,    // forward speed granted on exit
  mantleCooldown: 0.20,
  mantleDip: 5.5 * DEG,
  mantleRoll: 3.0 * DEG,

  /* ---------------------------------------------------------------- lean */
  leanMax: 0.30,           // lateral eye offset, metres
  leanRoll: 13.0 * DEG,
  leanSpring: 13.0,
  leanClearance: 0.22,     // keep this far off a wall when leaning

  /* ------------------------------------------------------------- stamina */
  tacSprintTapWindow: 0.28,
  tacSprintWindup: 0.35,   // ease into tac-sprint speed
  staminaMax: 1.0,
  staminaDrainTac: 1 / 4.5, // full stamina lasts 4.5 s of tac sprint
  staminaDrainSprint: 0.0,
  staminaRegen: 1 / 3.2,
  staminaRegenDelay: 0.9,

  /* -------------------------------------------------------------- camera */
  fovBase: 80,
  fovSprintKick: 6.0,
  fovTacKick: 9.0,
  fovSpeedKick: 2.0,
  fovAds: 58,
  fovSprintEase: 6.5,      // rad/s spring toward the sprint kick
  fovAdsEase: 12.0,
  pitchClamp: 89 * DEG,

  lookSensitivity: 0.0022, // radians per mouse unit (mirrors Input default)
  adsSensScale: 0.72,

  /* recoil: what fraction of a kick is permanently absorbed into the aim */
  recoilAbsorb: 0.34,
  recoilRecoverDelay: 0.06,
  recoilSpring: 9.0,
  recoilSpringDamping: 1.0,
  recoilMaxPitch: 22 * DEG,

  /* view bob — figure-of-eight, phase-locked to footsteps */
  bobAmpX: 0.028,
  bobAmpY: 0.020,
  bobAmpRoll: 0.9 * DEG,
  bobAdsScale: 0.10,       // near-zero while aiming
  bobAirScale: 0.0,
  bobSpeedRef: 4.4,
  bobMaxScale: 1.45,
  bobBlend: 9.0,           // how fast bob amplitude follows speed

  /* stride lengths per gait — these set footstep cadence */
  strideWalk: 1.85,
  strideSprint: 2.35,
  strideCrouch: 1.15,
  strideAds: 1.35,
  footstepPhase: (3 * Math.PI) / 4, // bottom of the figure-of-eight

  /* sway / lag */
  swayGain: 0.30,          // how much of the look delta the camera lags behind
  swaySpring: 11.0,
  swayMaxPitch: 3.2 * DEG,
  swayMaxYaw: 3.6 * DEG,
  swayAdsScale: 0.35,

  /* strafe roll */
  rollStrafe: 1.2 * DEG,
  rollSpring: 9.0,
  rollAdsScale: 0.45,

  /* breathing */
  breathAmpPitch: 0.16 * DEG,
  breathAmpYaw: 0.11 * DEG,
  breathRate: 0.26,        // Hz
  breathRate2: 0.41,
  breathAdsScale: 0.35,
  breathHoldScale: 0.18,   // while holding breath in ADS
  breathHoldTime: 0.45,

  /* landing */
  landDipMax: 0.155,
  landNodMax: 3.4 * DEG,
  landRefSpeed: 12.0,      // impact speed that produces the full dip
  landSpring: 15.0,
  landSpringDamping: 0.72,

  /* ------------------------------------------------------------- health */
  maxHealth: 100,
  regenDelay: 3.2,
  regenRate: 28,           // hp/s
  /* Fall damage is driven by fall HEIGHT, not impact speed — gravity is 2.2x
     here, so speed is a bad proxy for how far the player thinks they fell. */
  fallSafeHeight: 4.0,     // metres you can drop for free
  fallLethalHeight: 9.5,   // metres that kill outright
  fallDamageMax: 100,      // hp at the lethal height

  /* ------------------------------------------------------------- safety */
  killPlaneMargin: 30,     // metres below the level bounds before we respawn
  maxSafeSpeed: 60,
};

export const MovementDeg = DEG;
export default MovementConfig;
